/**
 * Prompt Management — prompt version lifecycle with variant A/B comparison.
 * Phase 4.7 — WQ-166
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { RefreshIcon } from '../common/Icons';
import { listAgents, type Agent } from '../../services/agentService';
import {
  listPrompts, createPrompt, activatePrompt, compareVariants,
  type PromptVersion,
} from '../../services/promptService';

const QUALITY_COLOR = (score: number | null): string => {
  if (score === null) return '#6b7280';
  if (score >= 0.8) return '#22c55e';
  if (score >= 0.6) return '#eab308';
  return '#dc2626';
};

const PromptManagement: React.FC = () => {
  const { authFetch, isLoading: authLoading } = useAuth();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [prompts, setPrompts] = useState<PromptVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ prompt_text: '', variant_name: '' });
  const [actionLoading, setActionLoading] = useState(false);

  // Comparison state
  const [variantA, setVariantA] = useState('');
  const [variantB, setVariantB] = useState('');
  const [comparison, setComparison] = useState<Record<string, unknown> | null>(null);
  const [compareLoading, setCompareLoading] = useState(false);

  useEffect(() => {
    if (!authLoading) {
      listAgents(authFetch).then(setAgents).catch(() => setError('Failed to load agents'));
    }
  }, [authLoading]);

  const fetchPrompts = async (agentId: string) => {
    if (!agentId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await listPrompts(authFetch, agentId);
      setPrompts(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load prompts');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (selectedAgentId) {
      fetchPrompts(selectedAgentId);
      setComparison(null);
      setVariantA('');
      setVariantB('');
    } else {
      setPrompts([]);
    }
  }, [selectedAgentId]);

  const handleActivate = async (id: string) => {
    setActionLoading(true);
    try {
      await activatePrompt(authFetch, id);
      await fetchPrompts(selectedAgentId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Activate failed');
    }
    setActionLoading(false);
  };

  const handleCreate = async () => {
    if (!createForm.prompt_text.trim()) return;
    setActionLoading(true);
    try {
      await createPrompt(authFetch, {
        agent_id: selectedAgentId,
        prompt_text: createForm.prompt_text.trim(),
        ...(createForm.variant_name ? { variant_name: createForm.variant_name.trim() } : {}),
      });
      setShowCreate(false);
      setCreateForm({ prompt_text: '', variant_name: '' });
      await fetchPrompts(selectedAgentId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Create failed');
    }
    setActionLoading(false);
  };

  const handleCompare = async () => {
    if (!variantA || !variantB || variantA === variantB) return;
    setCompareLoading(true);
    try {
      const result = await compareVariants(authFetch, selectedAgentId, variantA, variantB);
      setComparison(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Compare failed');
    }
    setCompareLoading(false);
  };

  const uniqueVariants = [...new Set(prompts.map(p => p.variant_name || 'default'))];

  if (!selectedAgentId) {
    return (
      <div>
        <div style={{ marginBottom: 16 }}>
          <select value="" onChange={e => setSelectedAgentId(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', minWidth: 240 }}>
            <option value="">Select an agent...</option>
            {agents.map(a => <option key={a.id} value={a.id}>{a.name} ({a.agent_type})</option>)}
          </select>
        </div>
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
          Select an agent to view its prompt versions.
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={selectedAgentId} onChange={e => setSelectedAgentId(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', minWidth: 240 }}>
          <option value="">Select an agent...</option>
          {agents.map(a => <option key={a.id} value={a.id}>{a.name} ({a.agent_type})</option>)}
        </select>
        <div style={{ flex: 1 }} />
        <button onClick={() => {
          const activeVersion = prompts.find(p => p.is_active) || prompts[0];
          if (activeVersion) {
            setCreateForm({ prompt_text: activeVersion.prompt_text, variant_name: '' });
          }
          setShowCreate(true);
        }} style={{
          fontSize: 13, padding: '6px 16px', borderRadius: 6,
          border: 'none', fontWeight: 500, cursor: 'pointer',
          background: 'var(--accent-primary, #3b82f6)', color: '#fff',
          display: 'inline-flex', alignItems: 'center', gap: 4,
        }}>
          + New Version
        </button>
        <button onClick={() => fetchPrompts(selectedAgentId)} style={{
          fontSize: 13, padding: '6px 12px', borderRadius: 6,
          border: '1px solid var(--border)', fontWeight: 500, cursor: 'pointer',
          background: 'var(--bg-secondary)', color: 'var(--text-primary)',
          display: 'inline-flex', alignItems: 'center', gap: 4,
        }}>
          <RefreshIcon size={14} /> Refresh
        </button>
      </div>

      {error && <div style={{ padding: 12, background: '#fef2f2', color: '#dc2626', borderRadius: 8, marginBottom: 16 }}>{error}</div>}

      {loading && prompts.length === 0 ? (
        <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-secondary)' }}>Loading prompts...</div>
      ) : prompts.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
          No prompt versions for this agent. Create the first version.
        </div>
      ) : (
        <>
          {/* Version Table */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  <th style={{ textAlign: 'center', padding: '8px 12px' }}>V#</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px' }}>Variant</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px' }}>Hash</th>
                  <th style={{ textAlign: 'center', padding: '8px 12px' }}>Active</th>
                  <th style={{ textAlign: 'center', padding: '8px 12px' }}>ISA Score</th>
                  <th style={{ textAlign: 'center', padding: '8px 12px' }}>Samples</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px' }}>By</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px' }}>Created</th>
                  <th style={{ textAlign: 'center', padding: '8px 12px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {prompts.map(p => (
                  <React.Fragment key={p.id}>
                    <tr style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer', background: expandedId === p.id ? 'var(--bg-secondary)' : 'transparent' }}
                      onClick={() => setExpandedId(expandedId === p.id ? null : p.id)}>
                      <td style={{ textAlign: 'center', padding: '8px 12px', fontWeight: 600 }}>{p.version}</td>
                      <td style={{ padding: '8px 12px' }}>{p.variant_name || 'default'}</td>
                      <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 11 }} title={p.prompt_hash}>
                        {p.prompt_hash.substring(0, 8)}
                      </td>
                      <td style={{ textAlign: 'center', padding: '8px 12px' }}>
                        {p.is_active && (
                          <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: '#22c55e20', color: '#22c55e', fontWeight: 600 }}>
                            Active
                          </span>
                        )}
                      </td>
                      <td style={{ textAlign: 'center', padding: '8px 12px' }}>
                        {p.isa_quality_score !== null ? (
                          <span style={{ color: QUALITY_COLOR(p.isa_quality_score), fontWeight: 600 }}>
                            {p.isa_quality_score.toFixed(2)}
                          </span>
                        ) : (
                          <span style={{ color: '#6b7280' }}>—</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'center', padding: '8px 12px', fontSize: 12 }}>{p.isa_sample_count}</td>
                      <td style={{ padding: '8px 12px', fontSize: 12 }}>{p.created_by}</td>
                      <td style={{ textAlign: 'right', padding: '8px 12px', fontSize: 12, color: 'var(--text-secondary)' }}>
                        {new Date(p.created_at).toLocaleDateString()}
                      </td>
                      <td style={{ textAlign: 'center', padding: '8px 12px' }} onClick={e => e.stopPropagation()}>
                        {!p.is_active && (
                          <button onClick={() => handleActivate(p.id)} disabled={actionLoading}
                            style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, border: '1px solid #22c55e', background: '#22c55e20', color: '#22c55e', cursor: 'pointer' }}>
                            Activate
                          </button>
                        )}
                      </td>
                    </tr>
                    {/* Expanded Prompt Text */}
                    {expandedId === p.id && (
                      <tr>
                        <td colSpan={9} style={{ padding: '12px 24px', background: 'var(--bg-secondary)' }}>
                          <pre style={{ fontSize: 12, overflow: 'auto', maxHeight: 300, padding: 12, background: 'var(--bg-tertiary)', borderRadius: 6, whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
                            {p.prompt_text}
                          </pre>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Variant Comparison */}
          {uniqueVariants.length >= 2 && (
            <div className="glass-card" style={{ padding: 16, marginTop: 16 }}>
              <h4 style={{ margin: '0 0 12px' }}>Variant Comparison</h4>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <select value={variantA} onChange={e => setVariantA(e.target.value)}
                  style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
                  <option value="">Variant A</option>
                  {uniqueVariants.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
                <span style={{ color: 'var(--text-secondary)' }}>vs</span>
                <select value={variantB} onChange={e => setVariantB(e.target.value)}
                  style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
                  <option value="">Variant B</option>
                  {uniqueVariants.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
                <button onClick={handleCompare} disabled={compareLoading || !variantA || !variantB || variantA === variantB}
                  style={{
                    fontSize: 13, padding: '6px 16px', borderRadius: 6,
                    border: 'none', fontWeight: 500, cursor: 'pointer',
                    background: 'var(--accent-primary, #3b82f6)', color: '#fff',
                  }}>
                  Compare
                </button>
              </div>

              {comparison && (
                <div style={{ marginTop: 12 }}>
                  <pre style={{ fontSize: 12, overflow: 'auto', maxHeight: 200, padding: 12, background: 'var(--bg-tertiary)', borderRadius: 6, margin: 0 }}>
                    {JSON.stringify(comparison, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Create Modal */}
      {showCreate && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setShowCreate(false)}>
          <div className="glass-card" style={{ padding: 24, minWidth: 500, maxWidth: 600 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px' }}>New Prompt Version</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <label style={{ fontSize: 13 }}>
                Agent: <strong>{agents.find(a => a.id === selectedAgentId)?.name || selectedAgentId}</strong>
              </label>
              {prompts.length > 0 && (
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', padding: '6px 10px', background: 'var(--bg-secondary)', borderRadius: 6 }}>
                  Editing from v{(prompts.find(p => p.is_active) || prompts[0])?.version ?? '?'}. Save to create v{Math.max(...prompts.map(p => p.version), 0) + 1}.
                </div>
              )}
              <label style={{ fontSize: 13 }}>
                Variant Name (optional)
                <input type="text" value={createForm.variant_name}
                  onChange={e => setCreateForm(f => ({ ...f, variant_name: e.target.value }))}
                  placeholder="e.g. concise, verbose, v2"
                  style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', marginTop: 4 }} />
              </label>
              <label style={{ fontSize: 13 }}>
                Prompt Text *
                <textarea value={createForm.prompt_text}
                  onChange={e => setCreateForm(f => ({ ...f, prompt_text: e.target.value }))}
                  rows={8} placeholder="Enter the prompt text..."
                  style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', marginTop: 4, resize: 'vertical', fontFamily: 'monospace', fontSize: 12 }} />
              </label>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                <button onClick={() => setShowCreate(false)} style={{
                  fontSize: 13, padding: '6px 12px', borderRadius: 6,
                  border: '1px solid var(--border)', fontWeight: 500, cursor: 'pointer',
                  background: 'var(--bg-secondary)', color: 'var(--text-primary)',
                }}>Cancel</button>
                <button onClick={handleCreate} disabled={actionLoading || !createForm.prompt_text.trim()} style={{
                  fontSize: 13, padding: '6px 16px', borderRadius: 6,
                  border: 'none', fontWeight: 500, cursor: 'pointer',
                  background: 'var(--accent-primary, #3b82f6)', color: '#fff',
                }}>
                  Create Version
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromptManagement;
